import React, { useState, useRef } from "react";

import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { toast } from "react-toastify";

import SidebarView, { SidebarRef } from "../common/Sidebar";
import { videosService } from "./services";
import { episodeService } from "../Episode/services";
import { usersService } from "../common/Headerbar/services";
import GenerateCustomShortVideoModal from "./components/GenerateCustomShortVideoModal";
import CreditsWarningModal from "../common/CreditsWarningModal";
import { VideoCard } from "./parts/VideoCard";
import { VideoStatus } from "./parts/VideoStatus";
import { MainContentLayout } from "./parts/MainContentLayout";
import { API_ERROR_MESSAGE } from "../../constants";
import {
  CustomShortVideoSuggestion,
  CustomVideoModalStep,
  GenerateCustomShortFormVideoIdeasPayload,
} from "./types";

export default function Videos() {
  const { id: episodeId } = useParams();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const sidebarRef = useRef<SidebarRef>(null);
  const [creditsWarningModalOpen, setCreditsWarningModalOpen] =
    useState<boolean>(false);
  const [prompt, setPrompt] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [suggestedShortVideos, setSuggestedShortVideos] = useState<
    CustomShortVideoSuggestion[] | null
  >(null);
  const [currentStep, setCurrentStep] = useState<CustomVideoModalStep>("input");
  // fetch videos
  const {
    data,
    refetch,
    isLoading,
    error: videosError,
  } = useQuery({
    queryKey: ["getVideos", episodeId],
    queryFn: () => videosService.getVideos(episodeId),
  });

  // fetch episode details
  const { data: episode } = useQuery({
    queryKey: ["getEpisodeById", episodeId],
    queryFn: () => episodeService.getEpisodeById(episodeId),
  });

  // fetch user data for credits
  const { data: userData } = useQuery({
    queryKey: ["authenticated-user"],
    queryFn: () => usersService.fetchAuthenticardUser(),
  });

  const availableCredits =
    userData?.organization?.subscription?.available_credits || 0;
  const hasCredits = availableCredits >= 5;

  const generateCustomShortFormVideoIdeasMutation = useMutation({
    mutationKey: ["short-videos-ideas", episodeId],
    mutationFn: (payload: GenerateCustomShortFormVideoIdeasPayload) =>
      videosService.generateCustomShortFormVideoIdeas(
        episodeId || "",
        payload.prompt,
        payload.allowAdditionalCharges,
      ),
    onSuccess: (videoData) => {
      setSuggestedShortVideos(videoData);
      setCurrentStep("suggested-videos");
    },
    onError: (err) => {
      if (err && typeof err === "object") {
        if ("statusCode" in err) {
          const apiError = err as {
            statusCode?: number;
            message?: string;
            error?: string;
          };
          if (apiError.statusCode === 429) {
            if (apiError.error === API_ERROR_MESSAGE.INSUFFICIENT_BALANCE) {
              toast.error(`${apiError.error}: ${apiError.message}`);
            } else if (
              apiError.error ===
              API_ERROR_MESSAGE.MAXIMUM_NUMBER_OF_PROMPTS_REACHED
            ) {
              setCreditsWarningModalOpen(true);
            }
          }
        }
      }
    },
  });

  const episodeName = episode?.name || "";

  // Count generating videos
  const generatingCount =
    data?.videos?.filter((video) => video.status === "generating" || !video.url)
      ?.length || 0;
  const hasGeneratingVideos = generatingCount > 0;

  // Count ready videos
  const readyCount =
    data?.videos?.filter((video) => video.url && video.status !== "generating")
      ?.length || 0;
  const hasReadyVideos = readyCount > 0;

  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  const handleRefreshVideos = async () => {
    setIsRefreshing(true);
    try {
      const startTime = Date.now();
      await refetch();
      const elapsedTime = Date.now() - startTime;
      const minDelay = 500;
      if (elapsedTime < minDelay) {
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), minDelay - elapsedTime);
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleConfirmCreditsWarning = () => {
    generateCustomShortFormVideoIdeasMutation.mutate({
      prompt,
      allowAdditionalCharges: true,
    });
  };

  const renderVideoCards = () => {
    return (
      <VideoCard
        isLoading={isLoading}
        setModalOpen={setModalOpen}
        data={data || { videos: [] }}
        episodeId={episodeId || ""}
      />
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <SidebarView pageName={`Episode - ${episodeName}`} ref={sidebarRef} />

      <MainContentLayout
        error={!!videosError}
        isLoading={isLoading}
        renderVideoCards={renderVideoCards}
        episodeName={episodeName}
        episodeId={episodeId || ""}
        handleMobileMenuToggle={handleMobileMenuToggle}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <VideoLibraryIcon
            color="primary"
            sx={{ fontSize: { xs: 20, sm: 24 } }}
          />
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
              lineHeight: 1.2,
            }}
          >
            Short Videos
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {hasGeneratingVideos && (
              <VideoStatus readyCount={generatingCount} status="pending" />
            )}
            {hasReadyVideos && (
              <VideoStatus readyCount={readyCount} status="ready" />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            flexShrink: 0,
          }}
        >
          <Tooltip title="Refresh videos">
            <IconButton
              onClick={handleRefreshVideos}
              disabled={isRefreshing}
              size="small"
              sx={{
                border: 1,
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              {isRefreshing ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RefreshIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              )}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            onClick={() => setModalOpen(true)}
            size="small"
            startIcon={<AutoAwesomeIcon />}
            sx={{
              minWidth: { xs: "auto", sm: "auto" },
              py: { xs: 0.75, sm: 1 },
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            Generate custom video
          </Button>
        </Box>
      </MainContentLayout>

      <GenerateCustomShortVideoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccessfullySaved={() => {
          setModalOpen(false);
          refetch();
        }}
        episode={episode}
        prompt={prompt}
        setPrompt={setPrompt}
        hasCredits={hasCredits}
        setError={setError}
        error={error}
        generateCustomShortFormVideoIdeasMutation={
          generateCustomShortFormVideoIdeasMutation
        }
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        suggestedShortVideos={suggestedShortVideos}
        setSuggestedShortVideos={setSuggestedShortVideos}
      />

      <CreditsWarningModal
        open={creditsWarningModalOpen}
        onClose={() => setCreditsWarningModalOpen(false)}
        onConfirm={handleConfirmCreditsWarning}
      />
    </Box>
  );
}
